import { useState, useCallback } from 'react';

// Services.
import PrivateWealthTeamsService from '../../services/PrivateWealthTeamsService';

const useAccolades = ({
  team,
  session,
  openConfirmDialog,
}) => {
  const [accolades, setAccolades] = useState([]);
  const [fetchingAccolades, setFetchingAccolades] = useState(false);
  const [fetchStatusAccolades, setFetchStatusAccolades] = useState('idle');
  const [excludedAccolades, setExcludedAccolades] = useState([]);
  const [fetchingExcludedAccolades, setFetchingExcludedAccolades] = useState(false);
  const [fetchStatusExcludedAccolades, setFetchStatusExcludedAccolades] = useState('idle');
  const [updatingAccolade, setUpdatingAccolade] = useState(false);

  const fetchAccolades = useCallback(async () => {
    setFetchingAccolades(true);
    setFetchStatusAccolades('fetching');
    try {
      const response = await PrivateWealthTeamsService.accolades(team.id, session);
      const { accolades, status } = response.data;
      setAccolades(accolades);
      setFetchStatusAccolades(status); // 'fetched'
    } catch (error) {
      console.error('fetchAccolades#error', JSON.stringify(error));
    } finally {
      setFetchingAccolades(false);
    }
  }, [team?.id, session]);

  const fetchExcludedAccolades = useCallback(async () => {
    setFetchingExcludedAccolades(true);
    setFetchStatusExcludedAccolades('fetching');
    try {
      const response = await PrivateWealthTeamsService.accolades(team.id, session, { exclude: true });
      const { accolades, status } = response.data;
      setExcludedAccolades(accolades);
      setFetchStatusExcludedAccolades(status); // 'fetched'
    } catch (error) {
      console.error('fetchAccolades#error', JSON.stringify(error));
    } finally {
      setFetchingExcludedAccolades(false);
    }
  }, [team?.id, session]);

  const handleUpdateAccoladeAssociation = useCallback(async (accoladeId, { remove = false }) => {
    setUpdatingAccolade(true);
    try {
      await PrivateWealthTeamsService.updateAccoladeTeamId(
        accoladeId,
        session,
        { params: { team_id: team.id, remove } }
      );
      await Promise.all([
        fetchAccolades(),
        fetchExcludedAccolades(),
      ]);
    } catch (error) {
      console.error('handleUpdateAccoladeAssociation#error', error);
    } finally {
      setUpdatingAccolade(false);
    }
  }, [team?.id, session, fetchAccolades]);

  const handleRemoveAccoladeFromTeam = useCallback((accoladeId) => {
    openConfirmDialog?.({
      title: 'Remove Accolade',
      message: 'Are you sure you want to remove this accolade from team?',
      confirmText: 'Remove',
      confirmColor: 'secondary',
      onConfirm: () => handleUpdateAccoladeAssociation(accoladeId, { remove: true }),
    });
  }, [team?.id, session, openConfirmDialog]);

  const handleAddAccoladeToTeam = useCallback((accoladeId) => {
    handleUpdateAccoladeAssociation(accoladeId, { remove: false });
  }, [team?.id, session]);

  return {
    accolades,
    fetchingAccolades,
    fetchStatusAccolades,
    excludedAccolades,
    fetchingExcludedAccolades,
    fetchStatusExcludedAccolades,
    updatingAccolade,

    fetchAccolades,
    fetchExcludedAccolades,
    handleAddAccoladeToTeam,
    handleRemoveAccoladeFromTeam,
  };
};

export default useAccolades;
